import { useCallback, useEffect, useState } from 'react';

import {
  createNoticeCompanyComment,
  listCompanyCommentsHistory,
  listNoticeCompanyComments,
} from '../../../../../../api/theHive/companyComments';
import {
  CompanyCommentData,
  ListCompanyCommentsResponse,
} from '../../../../../../api/theHive/companyComments/types';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';

const PER_PAGE = 50;

const extractComments = (
  response: ListCompanyCommentsResponse
): CompanyCommentData[] => {
  if (response instanceof Error) throw response;
  if (response.detail.description) {
    throw new Error(response.detail.description);
  }
  return response.data ?? [];
};

interface UseCompanyCommentsParams {
  noticeId: number;
  licensedCompanyId: number | undefined;
  enabled: boolean;
}

interface UseCompanyCommentsResult {
  comments: CompanyCommentData[];
  loading: boolean;
  submitting: boolean;
  submitComment: (body: string) => Promise<boolean>;
}

/**
 * Fio de comentários da credenciada neste edital.
 *
 * A LEITURA falha em silêncio: comentário é dado suplementar e a tela precisa
 * continuar utilizável se o endpoint cair (ou, hoje, se ainda não existir). Há
 * precedente na tela de análise, onde os documentos por critério seguem a mesma
 * regra — "falha aqui não bloqueia a página". A ESCRITA reporta erro, porque o
 * operador precisa saber que o texto não foi salvo.
 */
export const useCompanyComments = ({
  noticeId,
  licensedCompanyId,
  enabled,
}: UseCompanyCommentsParams): UseCompanyCommentsResult => {
  const [comments, setComments] = useState<CompanyCommentData[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const canRequest = enabled && !!licensedCompanyId && !Number.isNaN(noticeId);

  const fetchComments = useCallback(async (): Promise<void> => {
    if (!canRequest || !licensedCompanyId) return;
    setLoading(true);
    try {
      const response = await listNoticeCompanyComments(
        noticeId,
        licensedCompanyId,
        1,
        PER_PAGE
      );
      setComments(extractComments(response));
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [canRequest, noticeId, licensedCompanyId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const submitComment = async (body: string): Promise<boolean> => {
    if (!licensedCompanyId) return false;
    setSubmitting(true);
    try {
      const response = await createNoticeCompanyComment(
        noticeId,
        licensedCompanyId,
        { body }
      );
      if (response instanceof Error) throw response;
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      handleSnackbar('Comentário registrado!', false);
      await fetchComments();
      return true;
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return { comments, loading, submitting, submitComment };
};

interface UseCommentsHistoryResult {
  history: CompanyCommentData[];
  loading: boolean;
}

/**
 * Comentários da mesma credenciada em OUTROS editais — o histórico que segue o
 * profissional. Também falha em silêncio, pelo mesmo motivo.
 */
export const useCompanyCommentsHistory = (
  licensedCompanyId: number | undefined,
  currentNoticeId: number,
  enabled: boolean
): UseCommentsHistoryResult => {
  const [history, setHistory] = useState<CompanyCommentData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !licensedCompanyId) {
      setHistory([]);
      return;
    }
    let active = true;
    setLoading(true);
    listCompanyCommentsHistory(licensedCompanyId, 1, PER_PAGE)
      .then((response) => {
        if (!active) return;
        // O edital atual já aparece no fio principal.
        setHistory(
          extractComments(response).filter(
            (item) => item.notice_id !== currentNoticeId
          )
        );
      })
      .catch(() => {
        if (active) setHistory([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    // eslint-disable-next-line consistent-return
    return () => {
      active = false;
    };
  }, [enabled, licensedCompanyId, currentNoticeId]);

  return { history, loading };
};
